import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Building2 } from 'lucide-react';
import { useLocationCurrency } from '@/hooks/useLocationCurrency';

type PlanKey = 'free' | 'basic' | 'intermediate' | 'advanced';
type BillingCycle = 'monthly' | 'yearly';

// Yearly = 10 months price (2 months free)
const YEARLY_MULTIPLIER = 10;

// Fixed regional pricing — exact prices per region, not currency conversions
const regionalPricing: Record<string, Record<PlanKey, { price: number; currency: string }>> = {
  NGN: {
    free:         { price: 0,    currency: '₦'   },
    basic:        { price: 2500, currency: '₦'   },
    intermediate: { price: 5000, currency: '₦'   },
    advanced:     { price: 7500, currency: '₦'   },
  },
  GHS: {
    free:         { price: 0,  currency: 'GH₵' },
    basic:        { price: 30, currency: 'GH₵' },
    intermediate: { price: 60, currency: 'GH₵' },
    advanced:     { price: 90, currency: 'GH₵' },
  },
  KES: {
    free:         { price: 0,    currency: 'KSh' },
    basic:        { price: 350,  currency: 'KSh' },
    intermediate: { price: 700,  currency: 'KSh' },
    advanced:     { price: 1050, currency: 'KSh' },
  },
  ZAR: {
    free:         { price: 0,   currency: 'R' },
    basic:        { price: 50,  currency: 'R' },
    intermediate: { price: 100, currency: 'R' },
    advanced:     { price: 150, currency: 'R' },
  },
  USD: {
    free:         { price: 0,  currency: '$' },
    basic:        { price: 5,  currency: '$' },
    intermediate: { price: 10, currency: '$' },
    advanced:     { price: 15, currency: '$' },
  },
  EUR: {
    free:         { price: 0,  currency: '€' },
    basic:        { price: 5,  currency: '€' },
    intermediate: { price: 10, currency: '€' },
    advanced:     { price: 15, currency: '€' },
  },
  GBP: {
    free:         { price: 0,  currency: '£' },
    basic:        { price: 4,  currency: '£' },
    intermediate: { price: 8,  currency: '£' },
    advanced:     { price: 12, currency: '£' },
  },
  CAD: {
    free:         { price: 0,  currency: 'C$' },
    basic:        { price: 7,  currency: 'C$' },
    intermediate: { price: 14, currency: 'C$' },
    advanced:     { price: 21, currency: 'C$' },
  },
  AUD: {
    free:         { price: 0,  currency: 'A$' },
    basic:        { price: 8,  currency: 'A$' },
    intermediate: { price: 16, currency: 'A$' },
    advanced:     { price: 24, currency: 'A$' },
  },
  INR: {
    free:         { price: 0,    currency: '₹' },
    basic:        { price: 420,  currency: '₹' },
    intermediate: { price: 840,  currency: '₹' },
    advanced:     { price: 1260, currency: '₹' },
  },
  BRL: {
    free:         { price: 0,  currency: 'R$' },
    basic:        { price: 25, currency: 'R$' },
    intermediate: { price: 50, currency: 'R$' },
    advanced:     { price: 75, currency: 'R$' },
  },
  MXN: {
    free:         { price: 0,   currency: 'MX$' },
    basic:        { price: 90,  currency: 'MX$' },
    intermediate: { price: 180, currency: 'MX$' },
    advanced:     { price: 270, currency: 'MX$' },
  },
  JPY: {
    free:         { price: 0,    currency: '¥' },
    basic:        { price: 750,  currency: '¥' },
    intermediate: { price: 1500, currency: '¥' },
    advanced:     { price: 2250, currency: '¥' },
  },
  CNY: {
    free:         { price: 0,   currency: 'CN¥' },
    basic:        { price: 36,  currency: 'CN¥' },
    intermediate: { price: 72,  currency: 'CN¥' },
    advanced:     { price: 108, currency: 'CN¥' },
  },
  UGX: {
    free:         { price: 0,     currency: 'USh' },
    basic:        { price: 18500, currency: 'USh' },
    intermediate: { price: 37000, currency: 'USh' },
    advanced:     { price: 55500, currency: 'USh' },
  },
  TZS: {
    free:         { price: 0,     currency: 'TSh' },
    basic:        { price: 13000, currency: 'TSh' },
    intermediate: { price: 26000, currency: 'TSh' },
    advanced:     { price: 39000, currency: 'TSh' },
  },
  XOF: {
    free:         { price: 0,    currency: 'CFA'  },
    basic:        { price: 3000, currency: 'CFA'  },
    intermediate: { price: 6000, currency: 'CFA'  },
    advanced:     { price: 9000, currency: 'CFA'  },
  },
  XAF: {
    free:         { price: 0,    currency: 'FCFA' },
    basic:        { price: 3000, currency: 'FCFA' },
    intermediate: { price: 6000, currency: 'FCFA' },
    advanced:     { price: 9000, currency: 'FCFA' },
  },
};

export default function Pricing() {
  const { t } = useTranslation();
  const { currency: detectedCurrency, loading: currencyLoading } = useLocationCurrency('EUR');
  const [activeCurrency, setActiveCurrency] = useState('EUR');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  useEffect(() => {
    if (!currencyLoading) {
      // Use detected currency if we have pricing for it, otherwise fall back to EUR
      setActiveCurrency(regionalPricing[detectedCurrency] ? detectedCurrency : 'EUR');
    }
  }, [detectedCurrency, currencyLoading]);

  const plans: PlanKey[] = ['free', 'basic', 'intermediate', 'advanced'];

  const planColors: Record<string, { bg: string; border: string; badge?: string }> = {
    free:         { bg: 'bg-gray-50',   border: 'border-gray-200'  },
    basic:        { bg: 'bg-blue-50',   border: 'border-blue-200'  },
    intermediate: { bg: 'bg-green-50',  border: 'border-green-200', badge: 'bg-green-500' },
    advanced:     { bg: 'bg-purple-50', border: 'border-purple-200' },
  };

  const getPlanPrice = (planKey: PlanKey): { display: string; perMonth?: string } => {
    if (currencyLoading) return { display: '…' };
    const pricing = regionalPricing[activeCurrency] ?? regionalPricing['EUR'];
    const entry = pricing[planKey];
    if (!entry) return { display: '…' };
    if (entry.price === 0) return { display: `${entry.currency}0` };

    if (billingCycle === 'yearly') {
      const yearly = entry.price * YEARLY_MULTIPLIER;
      return {
        display: `${entry.currency}${yearly.toLocaleString()}`,
        perMonth: `${entry.currency}${(yearly / 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo`,
      };
    }

    return { display: `${entry.currency}${entry.price.toLocaleString()}` };
  };

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-primary/10 via-background to-background py-16 sm:py-20 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
              {t('public.pricing.title')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('public.pricing.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Billing Cycle Toggle */}
      <section className="py-6 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3">
            <span
              className={`text-sm font-medium cursor-pointer ${billingCycle === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={billingCycle === 'yearly'}
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                billingCycle === 'yearly' ? 'bg-primary' : 'bg-input'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                  billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span
              className={`text-sm font-medium cursor-pointer flex items-center gap-2 ${billingCycle === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly
              <Badge className="bg-green-500 text-white text-xs py-0 px-1.5">
                2 months free
              </Badge>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 sm:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-7xl mx-auto">
            {plans.map((planKey) => {
              const plan = t(`public.pricing.plans.${planKey}`, { returnObjects: true }) as {
                name: string;
                price: string;
                description: string;
                features: string[];
              };
              const colors = planColors[planKey];
              const isPopular = planKey === 'intermediate';
              const priceInfo = getPlanPrice(planKey);

              return (
                <Card
                  key={planKey}
                  className={`relative flex flex-col border-2 ${colors.border} ${colors.bg} hover:shadow-lg transition-shadow ${
                    isPopular ? 'lg:scale-105 lg:-my-4' : ''
                  }`}
                >
                  {isPopular && (
                    <Badge className={`absolute -top-3 left-1/2 -translate-x-1/2 ${colors.badge} text-white`}>
                      {t('public.pricing.mostPopular')}
                    </Badge>
                  )}
                  <CardContent className="p-6 flex flex-col flex-1">
                    {/* Plan Header */}
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                      <div className="flex items-baseline justify-center space-x-1">
                        <span className="text-4xl font-bold text-primary">
                          {priceInfo.display}
                        </span>
                        <span className="text-muted-foreground">{billingCycle === 'yearly' ? '/year' : '/month'}</span>
                      </div>
                      {billingCycle === 'yearly' && priceInfo.perMonth && (
                        <p className="text-xs text-muted-foreground mt-1">{priceInfo.perMonth} equivalent</p>
                      )}
                      <p className="text-sm text-muted-foreground mt-2">{plan.description}</p>
                    </div>

                    {/* Features */}
                    <ul className="space-y-3 mb-8 flex-1">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start space-x-3">
                          <div className="w-5 h-5 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="h-3 w-3 text-secondary" />
                          </div>
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <Link to="/org-signup">
                      <Button
                        className={`w-full ${
                          isPopular
                            ? 'bg-secondary hover:bg-secondary/90 text-white'
                            : 'bg-primary hover:bg-primary/90'
                        }`}
                      >
                        {t('public.pricing.cta')}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Custom Plan Section */}
      <section className="py-16 sm:py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <Building2 className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              {t('public.pricing.custom.title')}
            </h2>
            <p className="text-muted-foreground mb-8">
              {t('public.pricing.custom.description')}
            </p>
            <Button variant="outline" size="lg" className="border-primary text-primary hover:bg-primary/10">
              {t('public.pricing.custom.button')}
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-primary-foreground mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
            Join thousands of organizations already using Tickally to manage their members and finances.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/org-signup">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                {t('public.home.orgs.cta')}
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10">
                {t('public.nav.howItWorks')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
